import React from "react";
import { createPortal } from "react-dom";
import { capitalizeWord } from "@hypexone/uicore";
import { WhitelabelEnvDTO } from "../../../constants/brandConfig";
import { BRAND_NAME, FAVICON } from "../../../constants/Branding";
import faviconDiamond from "../../../assets/images/common/faviconDiamond.png";
import faviconKhelo from "../../../assets/images/common/faviconKhelo.png";
import faviconLaser from "../../../assets/images/common/faviconLaser.png";
import faviconLotus from "../../../assets/images/common/faviconLotus.png";
import faviconTiger from "../../../assets/images/common/faviconTiger.png";
import faviconMain from "../../../assets/images/common/faviconMain.png";
import faviconAp from "../../../assets/images/common/faviconAp.png";
import faviconWazir from "../../../assets/images/common/faviconWazir.png";
import faviconRbc from "../../../assets/images/common/faviconRbc.png";
import faviconFp from "../../../assets/images/common/faviconFp.png";
import faviconSky from "../../../assets/images/common/faviconSky.png";
import faviconAura from "../../../assets/images/common/faviconAura.png";
import faviconRadhe from "../../../assets/images/common/faviconRadhe.png";
import faviconWin from "../../../assets/images/common/faviconWin.png";
import favicon10c from "../../../assets/images/common/favicon10c.png";

const SKIN_FAVICONS: Record<string, string> = {
  diamond: faviconDiamond,
  lotus: faviconLotus,
  laser: faviconLaser,
  tiger: faviconTiger,
  khelo: faviconKhelo,
  main: faviconMain,
  ap: faviconAp,
  wazir: faviconWazir,
  rbc: faviconRbc,
  fp: faviconFp,
  sky: faviconSky,
  radhe: faviconRadhe,
  win: faviconWin,
  aura: faviconAura,
  '10c': favicon10c,
};

export type SkinItem = NonNullable<WhitelabelEnvDTO["skins"]>[number];

const skinKey = (label: string) => label.toLowerCase().replace(/\s/g, "");
const envFaviconKey = skinKey(BRAND_NAME);

export const getSkinFavicon = (label: string, _disabled?: boolean) => {
  const key = skinKey(label);
  // Prefer the skin label map so FP (and other skins) don't inherit the
  // parent house favicon when the whitelabel env reuses e.g. One8ex assets.
  if (SKIN_FAVICONS[key]) return SKIN_FAVICONS[key];
  if (key === envFaviconKey) return FAVICON;
  return FAVICON;
};

export const redirectToSkin = (targetDomain: string) => {
  const jwtToken = sessionStorage.getItem("jwt_token");
  sessionStorage.clear();
  window.open(`http://${targetDomain}/login?authToken=${jwtToken}`, "_self");
};

type SkinsPickerPopupProps = {
  open: boolean;
  onClose: () => void;
  skins: SkinItem[];
};

const SkinsPickerPopup: React.FC<SkinsPickerPopupProps> = ({
  open,
  onClose,
  skins,
}) => {
  if (!open) return null;

  const handleSelect = (skin: SkinItem) => {
    if (skin.disabled) return;
    redirectToSkin(skin.domain);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-[calc(100%-32px)] max-w-[360px] rounded-[14px] border border-white/10 bg-[rgba(18,18,18,0.92)] p-3.5 text-white shadow-[-4px_0_20px_rgba(0,0,0,0.25),0_12px_32px_rgba(0,0,0,0.4)] backdrop-blur-[10px]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Choose Skin"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="m-0 text-[15px] font-bold tracking-[0.02em]">
            Choose Skin
          </h3>
          <button
            type="button"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border-0 bg-white/50 text-xl leading-none text-black hover:bg-white/20"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 max-[720px]:gap-1.5">
          {skins.map((skin) => {
            const favicon = getSkinFavicon(skin.label, skin.disabled);
            const selected = Boolean(skin.disabled);

            return (
              <button
                key={skin.label}
                type="button"
                className={[
                  "flex min-h-[84px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[10px] border border-white/[0.18] bg-transparent px-1.5 py-2.5 text-white transition-[background,transform,border-color,box-shadow] duration-200 ease-in-out max-[720px]:min-h-[76px] max-[720px]:rounded-lg max-[720px]:px-1 max-[720px]:py-2",
                  selected
                    ? "cursor-default border-white/95 bg-white/50 shadow-[0_0_0_1px_rgba(255,255,255,0.35),0_0_12px_rgba(255,255,255,0.25)]"
                    : "hover:scale-[1.04] hover:border-white/30 hover:bg-white/[0.12]",
                ].join(" ")}
                aria-current={selected ? "true" : undefined}
                onClick={() => handleSelect(skin)}
                aria-label={skin.label}
              >
                {favicon ? (
                  <img
                    src={favicon}
                    alt=""
                    className="pointer-events-none block h-9 w-9 object-contain max-[720px]:h-[30px] max-[720px]:w-[30px]"
                  />
                ) : null}
                <span className="text-center text-[11px] font-semibold leading-tight text-white/85">
                  {capitalizeWord(skin.label)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SkinsPickerPopup;
